// import Decimal from 'break_infinity.js';
import './smooth-progress';

import {css, html, nothing} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  interval,
  map,
  NEVER,
  Observable,
  of,
  scan,
  share,
  shareReplay,
  skip,
  Subject,
  Subscription,
  takeWhile,
  tap,
} from 'rxjs';

import {ActivityOption} from './activity-option.js';
import {ActivityQueueItem, Configuration} from './activity-queue-item.js';
import {Batch} from './batch.js';
import Decimal from './break_infinity.js';
import * as D from './decimal_utils';
import global_css from './global-css.js';
import {DynamicCost, evaluateCost} from './interfaces/dynamic-value.js';
import {Queueable} from './interfaces/queueable.js';
import {IValuable} from './interfaces/valuable.js';
import {ToolTippy} from './mixins/tooltippy.js';
import {ri} from './runner-idle.js';
import {ActivityName, ActivitySaveData} from './types/runner-idle.js';
import {UnlockableLitElement} from './unlockable-lit-element.js';
import {
  all$,
  any$,
  formatCostEffect,
  formatEng,
  formatTime,
  typedKeys,
} from './utils.js';
import {Vector} from './Vector';
import {and, any, not, or} from 'ramda';
import {persistObservable} from './decorators/persist.js';

/**
 * Something the player can do in the Activity Queue, but not manually.
 *
 * An Activity represents an activity that the player can engage in,
 * such as Resting, Walking, etc.
 * May have multiple options, such as Gather: Wood or Gather: Berries.
 */
@customElement('ri-activity')
export class Activity
  extends ToolTippy(UnlockableLitElement, {
    // delay: [0, 0],
    duration: [0, 0],
    interactive: true,
  })
  implements UnlockableLitElement, Queueable
{
  // #region Properties (53)

  /**
   * Backing field for `duration` (how many seconds this Activity takes).
   * By default `duration` does nothing special, but can be overriden by subclasses.
   */
  #duration = 1;
  // #durationSubscription?: Subscription;
  #enqueueSubscription!: Subscription;
  #maxConsecutiveUses = new Decimal(0);
  #onPerformSubscription!: Subscription;
  /**
   * Backing field for `options` (which options are currently available).
   * Any options here are available even if locked.
   */
  #options: string[] = [];
  /** Cache of the available options, for performance. */
  #optionsCache: string[] = [];
  /** Holds the timeout for #optionsCache, to allow clearing it. */
  #optionsCacheTimeout: ReturnType<typeof setTimeout> | null = null;
  /** All of the possible options for this Activity, if any. */
  #optionsData: Record<string, ActivityOption> = {};

  /** Backing field for tags. Any tags here are present on all instances. */
  protected override baseTags = new Set(['activity']);

  public static override styles = [
    global_css,
    css`
      div.drag,
      div.drag-helper.dragging {
        background-color: rgb(var(--value-color, initial), 1);
        font-size: 1rem;
        outline: 1px solid black;
        border-radius: 4px;
        padding: 2px 4px;
      }

      div.drag {
        transition-duration: 0.25s;
        cursor: move;
      }

      div.drag:hover {
        background-color: #0008;
        color: white;
      }

      div.drag-helper {
        display: none;
      }

      div.drag-helper.dragging {
        display: block;
        cursor: move;
      }
    `,
  ];

  /** The index of the currently selected option. */
  @property({type: Number}) public optionIndex = 0;
  /** A list of tags. Object with those tags are also options. */
  @property({type: Array}) public optionsByTag: string[] = [];
  @state() public level: Decimal = new Decimal(1);

  /** Used for saving if the player has ever been to a place where they could use this. Figure out a better way. */
  public _canUseHere = false;
  /** Can this Activity be partially performed? Currently broken, I think. */
  public allowPartial = false;
  // baseDuration$: Observable<number> = of(1);
  canUseHere$!: Observable<boolean>;
  public configuration: Configuration = {};
  /**
   * How many times has this been used in a row?
   * Tracks **most recent** usage, not most ever.
   * e.g. AAABAA would end with `A.consecutiveUses` of 2, not 3.
   */
  public consecutiveUses = new Decimal(0);
  public consecutiveUses$ = new BehaviorSubject(new Decimal(0));
  /** The cost to perform this Activity, if any.  */
  public cost: DynamicCost = new Map();
  cost$: Observable<DynamicCost> = new BehaviorSubject(new Map());
  /** A user-friendly description. */
  public description = 'Description';
  public override displayName = 'Activity';
  /** The number of seconds the Activity will take. */
  public duration = 1;
  /**
   * The effect performing this Activity has, if any.
   * This is only gaining/losing value in Gettables,
   * more esoteric effects should be handled by setting `onPerform`.
   */
  public effect: DynamicCost = new Map();
  effect$: Observable<DynamicCost> = new BehaviorSubject(new Map());
  /** A stream signaling a request to enqueue an ActivityQueueItem of this Activity */
  public enqueue$: Observable<Activity> = this.leftClick$.pipe(
    // No modifier keys
    filter(
      (event) =>
        !(event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
    ),
    map(() => this)
  );
  public level$: Observable<Decimal> = of(new Decimal(1));
  public maxConsecutiveUses: Decimal = new Decimal(0);
  public maxConsecutiveUses$!: Observable<Decimal>;
  public override name: ActivityName = 'rest';
  /** A stream of performances (specifically, step and optionindex info) */
  perform$: Subject<[number, number]> = new Subject();
  public repeatByDefault = true;
  /** The name of the Activity, if any, that this should replace upon unlock. */
  public replacesActivity: ActivityName | '' = '';
  /** How many times has this been successfully used? */
  public successfulUses$ = new BehaviorSubject(new Decimal(0));
  public timeSpent$!: Observable<Decimal>;
  /** How many times has this been used? Does not include when skipped due to missing requirements. */
  public uses$: BehaviorSubject<Decimal> = new BehaviorSubject(new Decimal(0));

  @persistObservable(1)
  public duration$!: Observable<number>;

  public get option() {
    return this.options[this.optionIndex];
  }

  public get options() {
    if (this.#optionsCache.length > 0) return this.#optionsCache;
    this.#optionsCache = [
      ...new Set([
        ...this.#options,
        ...Object.values(this.optionsData)
          .filter(
            (optionData) => optionData.canUseHere() && optionData.unlocked
          )
          .map((optionData) => optionData.name),
        ...this.optionsByTag.flatMap((tag: string) =>
          typedKeys(ri.resources).filter((resourceName) => {
            const resource = ri.resources[resourceName];
            return (
              // resource.unlocked &&
              resource.tags.has(tag) && resource.name in this.optionsData
            );
          })
        ),
      ]),
    ];
    if (this.#optionsCacheTimeout) clearTimeout(this.#optionsCacheTimeout);
    this.#optionsCacheTimeout = setTimeout(() => {
      this.#optionsCache = [];
    }, 1000);
    return this.#optionsCache;
  }

  public set options(value) {
    this.#options = value;
  }

  public get optionsData(): Record<string, ActivityOption> {
    return this.#optionsData;
  }

  public set optionsData(value: Record<string, ActivityOption>) {
    this.setOptionsData(value);
    // this._optionsData = value;
  }

  public get stepLength(): number {
    return Math.min(this.duration, 1);
  }

  public override get tags() {
    // const optionsCostEffects = Object.entries(this.optionsData)
    //   .filter(([optionName]) => this.options.includes(optionName))
    //   .flatMap(([, optionData]) =>
    //     [...optionData.cost, ...optionData.effect].map(([key]) => key)
    //   );
    return new Set([
      ...this.baseTags,
      ...[...this.cost, ...this.effect].map(([key]) => key),
      // ...optionsCostEffects,
    ]);
  }

  public override set tags(value) {
    this.baseTags = new Set([...this.baseTags, ...value]);
  }

  public get timeSpent() {
    return this.uses$.value.times(this.duration);
  }

  override get unlock$() {
    return super.unlock$;
  }

  /** MUST SET IN SETUP IF USING landmarkTags */
  override set unlock$(value: Observable<boolean>) {
    super.unlock$ = all$([value, this.canUseHere$.pipe(scan(or))]);
  }

  // #endregion Properties (53)

  // #region Constructors (1)

  constructor() {
    super();
    this.maxConsecutiveUses$ = this.consecutiveUses$.pipe(
      scan(D.max),
      distinctUntilChanged(),
      shareReplay(1)
    );
    // Time spent ignores partial perform
    this.timeSpent$ = D.product$([
      this.uses$,
      this.duration$.pipe(map((d) => new Decimal(d))),
    ]);
    // Level is 1+log_10(uses)
    // 1 at 0 uses
    // 2 at 10 uses
    // 3 at 100 uses
    // etc.
    this.level$ = this.uses$.pipe(
      map((uses: Decimal) =>
        uses.neq(0) ? new Decimal(uses.log(10)).plus(1).floor() : new Decimal(1)
      )
    );
  }

  // #endregion Constructors (1)

  // #region Public Methods (17)

  public override _updateTooltip() {
    // return;
    const optionsContainer =
      this.tooltip?.popper.querySelector('.options-container');
    if (optionsContainer && optionsContainer.scrollTop != 0) return;
    let costEffectString: string;
    if (this.options.length > 0) {
      costEffectString = '<div class="options-container">';
      for (const optionName of this.options) {
        const optionData = this.optionsData[optionName];
        if (!optionData) continue;
        costEffectString += `<br>${
          optionData?.displayName ?? ''
        }<br>${formatCostEffect({
          ...optionData,
          length: optionData.duration ?? this.duration,
        })}<br>
        Uses: ${optionData?.uses$.value?.toString() ?? '0'}<br>
        Successful Uses: ${
          optionData?.successfulUses$.value?.toString() ?? '0'
        }<br>`;
      }
      costEffectString += '</div>';
    } else {
      costEffectString = formatCostEffect(this, 1);
    }
    this.tooltip?.setContent(
      `${this.description}<br><br>${costEffectString}${
        this.tooltipContent() ? '<br><br>' + this.tooltipContent() : ''
      } <br><br>Uses: ${this.uses$.value} / ${
        10 ** Math.ceil(this.uses$.value.log10())
      } to next level
      <br>Successful Uses: ${this.successfulUses$.value}
      <br>Consecutive Uses: ${this.consecutiveUses$.value}/${
        this.maxConsecutiveUses
      }
      <br>Time Spent: ${formatTime(this.timeSpent)}<br>Tags: ${[
        ...this.baseTags,
      ]}`
    );
  }

  public canAfford(
    step: number,
    optionIndex: number,
    chanceRoll: number,
    dryRun = false
  ): boolean {
    if (!this.canUseHere(step, optionIndex)) {
      ri.log(
        `Cannot ${
          this.optionsData?.[this.options?.[optionIndex]]?.displayName ??
          this.displayName
        } here, need ${[
          ...this.landmarkTags,
          ...(this.optionsData?.[this.options?.[optionIndex]]?.landmarkTags ??
            []),
        ]
          .filter((tag) => !ri.currentLandmark?.tags.has(tag))
          .join(', ')}`,
        ['activity', 'error', this.name]
      );
      return false;
    }
    let cost = this.cost;
    // For Activities with options, override the cost with the selected option's cost.
    if (this.options.length > 0) {
      // TODO part of readding options
      const optionOverride = this.checkForOptionOverride(
        step,
        optionIndex,
        chanceRoll
      );
      cost = optionOverride.cost ?? this.cost;
    }
    for (const [key, {value}] of cost) {
      const val = new Decimal(value(step, optionIndex));
      const item = ri.get(key);
      if (!item) return false;
      if (val.gt(0)) {
        if (val.gt((item as IValuable).value)) {
          if (!dryRun) {
            ri.log(
              `Cannot ${this.displayName} - Not enough ${
                item.displayName
              }, need ${formatEng(val)} have ${formatEng(
                item.value
              )}. Need ${formatEng(val.minus(item.value))} more.`,
              [
                'activity',
                this.name,
                'mergeable',
                `merge_${this.name}`,
                'error',
              ]
            );
          }
          return false;
        }
      } else if (val.lt(0)) {
        if (
          val.times(-1).gt((item as IValuable & {vacancy: Decimal}).vacancy)
        ) {
          if (!dryRun) {
            ri.log(
              `Cannot ${this.displayName} - Too much ${
                item.displayName
              }, need ${formatEng(val.times(-1))} space have ${formatEng(
                (item as IValuable & {vacancy: Decimal}).vacancy
              )}. Need ${formatEng(
                (item as IValuable & {vacancy: Decimal}).vacancy
                  .plus(val)
                  .times(-1)
              )} less.`,
              [
                'activity',
                this.name,
                'mergeable',
                `merge_${this.name}`,
                'error',
              ]
            );
          }
          return false;
        }
      }
    }
    return true;
  }

  /** Does the current Landmark satisfy `this.landmarkTags` ? */
  public override canUseHere(
    step?: number,
    optionIndex?: number,
    point?: Vector
  ) {
    if (point === undefined) {
      point = ri.position;
    }
    // Always this.landmarkTags, but also the option's if this has options.
    const neededTags = new Set([
      ...this.landmarkTags,
      ...(optionIndex === undefined
        ? []
        : this.optionsData?.[this.options?.[optionIndex]]?.landmarkTags ?? []),
    ]);
    return (
      neededTags.size === 0 ||
      [...neededTags].every((tag) => ri.pointHasTag(point as Vector, tag))
    );
  }

  public checkForOptionOverride(
    step: number,
    optionIndex: number,
    chanceRoll: number
  ): ActivityOption {
    // TODO Rebuild options rxjs style
    if (this.options.length > 0) {
      const optionName = this.options[optionIndex];
      const option = this.optionsData[optionName];
      if (!option) {
        return {};
      }
      if (
        option.chance &&
        new Decimal(option.chance(step, optionIndex)).lt(chanceRoll)
      ) {
        this.cost$ = option.failCost$ ?? option.cost$;
        return {
          cancel: true,
          logString: `${option.displayName}`,
          takesTime: true,
          cost$: option.failCost$ ?? option.cost$,
          effect$: option.failEffect$ ?? of(new Map()),
        };
      }
      return option;
    }
    return {};
  }

  public override clearNew(): void {
    super.clearNew();
    if (Object.entries(this.optionsData).length > 0) {
      // Remove new from options on mouseover
      Object.entries(this.optionsData)
        .filter(([k]) => this.options.includes(k))
        .forEach(([, v]) => {
          v.new = false;
        });
      this.requestUpdate();
    }
    ri.requestUpdate();
  }

  // public override disconnectedCallback(): void {
  //   this.#enqueueSubscription?.unsubscribe();
  //   this.#onPerformSubscription?.unsubscribe();
  //   super.disconnectedCallback();
  // }

  /**
   * How long is a specific option of this Activity?
   * @param optionIndex The index of the option to get the length of.
   * @returns The length of the Activity, or the length of the option if `optionIndex` is provided and available.
   */
  public getOptionDuration(optionIndex: number) {
    if (this.options.length <= optionIndex) return this.duration;
    return this.optionsData[this.option]?.duration ?? this.duration;
  }

  /** Callback function when you can't afford the activity.
   *
   * @param ri The RunnerIdle instance
   * @param step The step number
   * @param optionIndex The index of the option
   * @returns `true` if it takes time, `false` if it can be skipped
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onCannotAfford(step: number, optionIndex: number) {
    // ri.log(`Cannot ${this.displayName} - ${}`, ['activity', this.name]);
    console.log(`%cCannot afford`, 'color: red');
    return false;
  }

  /**
   * Post-perform hook.
   * Override this to create special effects
   * outside the cost/effect system.
   *
   * @param _step The step of this Activity that was just performed.
   * @param _optionIndex What option, if any, is being used.
   */
  public onPerform(step: number, optionIndex: number) {
    if (optionIndex < 0) return;
    this.optionsData[this.options[optionIndex]]?.onPerform(step, optionIndex);
  }

  /**
   * Do the thing!
   *
   * Checks if an Activity is possible, can be afforded,
   * passes random chance rolls, etc,
   * and performs it if warranted.
   *
   * @param step The step of this Activity to perform.
   * @param optionIndex What option, if any, is being used.
   * @returns true if the activity was successfully performed, onCannotAfford(args) otherwise
   */
  public perform(
    step: number,
    optionIndex: number,
    configuration: Configuration
  ): boolean {
    console.log(`%cPERFORM ${this.name}`, 'font-weight: bold');
    // Travel activities cannot be done if the player is at the target position
    if (this.tags.has('travel') && ri.targetPosition.eq(ri.position))
      return false;
    const chanceRoll = Math.random();
    // If this Activity cannot be partially done and this is the first step,
    // check if we can afford the entire Activity.
    let affordableStep = step;
    if (!this.allowPartial && step == 1) affordableStep = -1;

    if (this.canAfford(affordableStep, optionIndex, chanceRoll)) {
      // console.log("Can afford, let's do it!");
      const optionName = this.options[optionIndex];
      const optionOverride = this.checkForOptionOverride(
        step,
        optionIndex,
        chanceRoll
      );
      const logString = optionOverride.logString ?? '';
      const cost = optionOverride.cost ?? this.cost;
      const effect = optionOverride.effect ?? this.effect;

      const evaluatedCost = evaluateCost(
        cost,
        step,
        optionIndex,
        configuration
      );
      const evaluatedEffect = evaluateCost(
        effect,
        step,
        optionIndex,
        configuration
      );

      // Log first so that the values are available still
      ri.logCostEffect(this, logString, evaluatedCost, evaluatedEffect, [
        'activity',
      ]);
      // console.log(
      //   `costs for ${this.name}: ${Array.from(
      //     evaluatedCost,
      //     ([k, v]) => `${k}: ${v.value}`
      //   ).join(', ')}`
      // );
      // Pay all the costs.
      for (const [key, v] of evaluatedCost) {
        ri.gain(key, -v.value);
      }
      // console.log(
      //   `effects for ${this.name}: ${Array.from(
      //     evaluatedEffect,
      //     ([k, v]) => `${k}: ${v.value}`
      //   ).join(', ')}`
      // );
      // Perform the value-based effects.
      for (const [key, v] of evaluatedEffect) {
        ri.gain(key, v.value);
      }
      // Let everyone know this performed
      this.perform$.next([step, optionIndex]);
      // Update uses if this is the first step of the activity
      if (step === 0) {
        this.uses$.next(this.uses$.value.plus(1));
        // TODO make this more streamy
        if (ri.lastCompletedActivity !== this) {
          ri.lastCompletedActivity?.consecutiveUses$.next(new Decimal(0));
          ri.lastCompletedActivity = this;
        }
        this.consecutiveUses$.next(this.consecutiveUses$.value.plus(1));
        if (optionName) {
          this.optionsData[optionName].uses$.next(
            this.optionsData[optionName].uses$.value.plus(1)
          );
        }
        if (!optionOverride.cancel) {
          this.successfulUses$.next(this.successfulUses$.value.plus(1));
          if (optionName) {
            this.optionsData[optionName].successfulUses$.next(
              this.optionsData[optionName].successfulUses$.value.plus(1)
            );
          }
        }
      }
      return true;
    }
    // Can't afford it.
    return this.onCannotAfford(step, optionIndex);
  }

  public override render() {
    this._updateTooltip();
    return html`
      <div class="drag activity fancy">
        LVL${this.level} ${this.displayName}
        ${this.options.length > 0
          ? html`
              <select
                name="options"
                class=${this.options[this.optionIndex]}
                @click=${(e: Event) => e.stopPropagation()}
                @change=${(e: Event) =>
                  (this.optionIndex = Number(
                    (e.target as HTMLOptionElement).value
                  ))}
              >
                ${this.options.map(
                  (option, index) => html`
                    <option
                      value=${index}
                      class=${this.options[index]}
                      ?selected=${index == this.optionIndex}
                    >
                      ${option}
                    </option>
                  `
                )}
              </select>
            `
          : nothing}
      </div>
    `;
  }

  public replace(activity: Activity) {
    ri.activityQueue.forEach((queueItem) => {
      if (queueItem.activity == activity) {
        queueItem.activity = this;
      }
    });
    ri.loadouts.forEach((loadout) =>
      loadout.forEach((queueItem) => {
        if (queueItem.activity == activity) {
          queueItem.activity = this;
        }
      })
    );
  }

  public replaceQueueItemsWith(queueItems: ActivityQueueItem[]) {
    this.toggleHiddenByPlayer(true);
    ri.activityQueue = ri.activityQueue.flatMap(
      (topQueueItem: ActivityQueueItem) => {
        // Replace only this Activity
        if (topQueueItem.activity === this) {
          // Creates a new Array of the queueItems
          // repeated the number of times this Activity is done
          // .map instead of .fill to create new ActivityQueueItem s
          return new Array(topQueueItem.repeat).fill('').flatMap(() =>
            queueItems.map((innerQueueItem) => {
              const newActivityQueueItem = document.createElement(
                'ri-activity-queue-item'
              ) as ActivityQueueItem;
              newActivityQueueItem.activity = innerQueueItem.activity;
              newActivityQueueItem.option = innerQueueItem.option;
              return newActivityQueueItem;
            })
          );
        }
        return topQueueItem;
      }
    );
    ri.loadouts = ri.loadouts.map((loadout) => {
      return loadout.flatMap((topQueueItem: ActivityQueueItem) => {
        // Replace only this Activity
        if (topQueueItem.activity === this) {
          // Creates a new Array of the queueItems
          // repeated the number of times this Activity is done
          // .map instead of .fill to create new ActivityQueueItem s
          return new Array(topQueueItem.repeat).fill('').flatMap(() =>
            queueItems.map((innerQueueItem) => {
              const newActivityQueueItem = document.createElement(
                'ri-activity-queue-item'
              ) as ActivityQueueItem;
              newActivityQueueItem.activity = innerQueueItem.activity;
              newActivityQueueItem.option = innerQueueItem.option;
              return newActivityQueueItem;
            })
          );
        }
        return topQueueItem;
      });
    });
    // DRY this out
    Object.values(ri.batches).forEach((batch: Batch) => {
      batch.activityQueue = batch.activityQueue.flatMap(
        (topQueueItem: ActivityQueueItem) => {
          // Replace only this Activity
          if (topQueueItem.activity === this) {
            // Creates a new Array of the queueItems
            // repeated the number of times this Activity is done
            // .map instead of .fill to create new ActivityQueueItem s
            return new Array(topQueueItem.repeat).fill('').flatMap(() =>
              queueItems.map((innerQueueItem) => {
                const newActivityQueueItem = document.createElement(
                  'ri-activity-queue-item'
                ) as ActivityQueueItem;
                newActivityQueueItem.activity = innerQueueItem.activity;
                newActivityQueueItem.option = innerQueueItem.option;
                return newActivityQueueItem;
              })
            );
          }
          return topQueueItem;
        }
      );
    });
  }

  public resetToInitials() {
    this.uses$.next(new Decimal(0));
  }

  public setOptionsData(optionsData: Partial<ActivityOption>) {
    this.#optionsData = Object.fromEntries(
      Object.entries(optionsData).map(([name, optionsData]) => {
        const option: ActivityOption =
          document.createElement('ri-activity-option');
        option.setup({
          ...(optionsData as {}),
          name: name,
          displayName: `${this.displayName}: ${name}`,
          activity: this,
        });
        return [name, option];
      })
    );
  }

  public override setup(overrides: Partial<Activity> = {}) {
    // console.log('Setting up Activity', this.name);
    this.canUseHere$ = ri.position$.pipe(
      map(
        (pos) => this._canUseHere || this.canUseHere(undefined, undefined, pos)
      ),
      tap((val) => (this._canUseHere = val)),
      distinctUntilChanged(),
      shareReplay(1)
    );
    super.setup(overrides);
    if (
      !(
        overrides.unlock$ ||
        overrides.requiredResources$ ||
        overrides.requiredStats$ ||
        overrides.requiredLandmarks$
      )
    ) {
      if (overrides.optionsData) {
        // If this has options but no other requirements,
        // it unlocks if any of the options are unlocked.
        super.setup({
          conditionString: `Unlock something to ${this.displayName}`,
          unlock$: interval(1000).pipe(
            map(() => this.options.length > 0),
            takeWhile(not, true)
          ),
        });
      }
      if (overrides.landmarkTags) {
        // If this has landmarkTags  but no other requirements,
        // that takes care of it.
        super.setup({
          unlock$: of(true),
        });
      }
    }
    this.setOptionsData(overrides.optionsData ?? {});
    this.subscribe('cost', this.cost$);
    this.subscribe('effect', this.effect$);
    this.subscribe('level', this.level$);
    this.subscribe('consecutiveUses', this.consecutiveUses$);
    this.subscribe('maxConsecutiveUses', this.maxConsecutiveUses$);
    this.subscribe('duration', this.duration$);
    this.#enqueueSubscription = this.enqueue$.subscribe(ri.enqueueActivity);
    this.#onPerformSubscription = this.perform$.subscribe(
      ([step, optionIndex]) => {
        // console.log('Performing', this.name, step, optionIndex);
        this.onPerform(step, optionIndex);
      }
    );
  }

  public override toJSON(): Partial<ActivitySaveData> {
    return {
      ...super.toJSON(),
      uses: this.uses$.value,
      successfulUses: this.successfulUses$.value,
      consecutiveUses: this.consecutiveUses,
      maxConsecutiveUses: this.maxConsecutiveUses,
      _canUseHere: this._canUseHere,
      optionsData: Object.values(this.optionsData).map(
        (option: ActivityOption) => option.toJSON()
      ),
    };
  }

  public unlockOption(optionName: string) {
    this.#options.push(optionName);
    this.requestUpdate();
  }

  // #endregion Public Methods (17)

  // #region Protected Methods (1)

  protected override onUnlock(): void {
    super.onUnlock();
    if (this.replacesActivity != '') {
      this.replace(ri.activities[this.replacesActivity]);
      ri.log(
        `Replaced ${this.displayName} with ${
          ri.activities[this.replacesActivity].displayName
        }`,
        [`activity`, this.name, 'persistent']
      );
    }
  }

  // #endregion Protected Methods (1)
}
